import { type CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, combineLatest, map, type Observable, startWith, Subject, tap, throttleTime } from 'rxjs';

const PAGE_SIZE = 70;
const LOAD_MORE_THRESHOLD = 0.8;

/**
 * Data source for tables with virtual scroll and fixed item size.
 * It listens to the scroll events of the viewport and emits the data slice based on the current scroll position.
 *
 * Make sure table has the following:
 *
 * 1. [multiTemplateDataRows]="true"
 * 2. Order of the rows in the template:
 *
 * ```html
 * <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true" />
 * <mat-row
 *   [style.height.px]="dataSource.offset$ | async"
 *   *matRowDef="let row; columns: []; when: placeholderWhen"
 * />
 * <mat-row *matRowDef="let row; columns: displayedColumns;" />
 * ```
 * 3. The placeholder row is used to create the offset at the top of the table based on the current scroll position.
 * This is necessary to keep the scrollbar size consistent with the total number of items in the data source.
 * ```ts
 * protected placeholderWhen(index: number, _: any) {
 *   return index === 0;
 * }
 * ```
 * 4. Datasource is replaced with the `FixedSizeItemVirtualViewportTableDataSource` and `itemSize` and `viewport` properties are set.
 */
export class FixedSizeItemVirtualViewportTableDataSource<T> extends MatTableDataSource<T> {
  itemSize?: number;
  lastIndex = 0;
  offset$ = new BehaviorSubject<number>(0);
  thresholdReached$: Observable<void>;
  viewport?: CdkVirtualScrollViewport;

  protected threshold$ = new Subject<void>();

  constructor(initialData?: T[]) {
    super(initialData);

    this.thresholdReached$ = this.threshold$.pipe(throttleTime(1000, undefined, { leading: true, trailing: true }));
  }

  override connect() {
    if (!this.itemSize || !this.viewport) {
      throw new Error('ItemSize and Viewport must be provided');
    }

    const index$ = this.viewport.scrolledIndexChange.pipe(
      startWith(0),
      tap(index => {
        this.lastIndex = index;
        this.offset$.next(index * this.itemSize!);

        const bottom = this.viewport?.measureScrollOffset('bottom') ?? 0;
        const totalContentSize = (this.data.length * this.itemSize!) || 0;
        const viewportSize = this.viewport!.getViewportSize();
        const currentScrollPosition = totalContentSize - bottom;

        if (currentScrollPosition <= 0 || totalContentSize === 0) {
          return;
        }

        if (
          currentScrollPosition >= totalContentSize * LOAD_MORE_THRESHOLD ||
          (bottom < this.itemSize! && currentScrollPosition > viewportSize)
        ) {
          this.threshold$.next();
        }
      }),
    );

    return combineLatest([super.connect(), index$]).pipe(
      map(([data, index]) => data
        .slice(index, index + PAGE_SIZE)
        .map((item, _index) => ({
          ...item,
          _index: index + _index, // to keep track of the original index
        })),
      ),
    ) as BehaviorSubject<T[]>;
  }
}
